// ========================================
// Role Permissions — cache en cliente de la matriz configurada en
// #permisos-roles (GlobalSettings.rolePermissions), usada para decidir qué
// enlaces del sidebar y qué widgets de los dashboards de departamento se
// muestran a cada rol. Fuente de verdad: GET /api/role-permissions.
// ========================================

const RolePermissions = {
    matrix: {},
    _loadPromise: null,

    // Traduce un `data-view` de sidebar/router a la clave de sección que usa
    // la matriz — no siempre es 1:1: 'calificaciones' y 'tutorias' despachan
    // a una vista de admin o de alumno según el rol (ver js/app.js `this.views`).
    keyForRoute(route, role) {
        switch (route) {
            case 'one-to-one':
            case 'puertas-abiertas': return 'leads';
            case 'academico': return 'academico-dash';
            case 'alumnocrm': return 'alumnocrm-dash';
            case 'rrhh': return 'rrhh-dash';
            case 'logistica': return 'logistica-dash';
            case 'calificaciones': return role === 'student' ? 'calificaciones-alumno' : 'calificaciones-admin';
            case 'tutorias': return 'tutorias-alumno';
            default: return route;
        }
    },

    async load() {
        if (this._loadPromise) return this._loadPromise;
        this._loadPromise = (async () => {
            try {
                const token = AuthManager.getCsrfToken();
                const response = await fetch('/api/role-permissions', {
                    headers: { 'X-SICAF-CSRF': token }
                });
                const data = await response.json();
                this.matrix = (data.success && data.permissions) ? data.permissions : {};
            } catch (error) {
                console.error('Error al cargar la matriz de permisos por rol:', error);
                this.matrix = {};
            }
            return this.matrix;
        })();
        return this._loadPromise;
    },

    // true = el rol actual puede ver esa clave de sección de la matriz
    // (no un `data-view`, ver canView() para eso). Admin siempre puede, para
    // no poder quedarse fuera de su propia herramienta de permisos. Una
    // sección que la matriz no tiene configurada (fila inexistente) se deja
    // visible por defecto — evita que secciones nuevas u olvidadas
    // desaparezcan para todo el mundo hasta que un admin las repase.
    // The per-person grant (Ajustes de la ficha) that also opens a matrix section.
    moduleForSection(sectionKey) {
        return {'profesor-ficha':'profesores','alumno-ficha':'alumnado','calificaciones-admin':'calificaciones','academico-dash':'academico','alumnocrm-dash':'alumnocrm','rrhh-dash':'rrhh','logistica-dash':'logistica','calendario-formacion':'calendario'}[sectionKey] || sectionKey;
    },

    canViewSection(sectionKey) {
        const user = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (!user) return false;
        if (user.role === 'admin') return true;
        const module = {'profesor-ficha':'profesores','alumno-ficha':'alumnado','calificaciones-admin':'calificaciones','academico-dash':'academico','alumnocrm-dash':'alumnocrm','rrhh-dash':'rrhh','logistica-dash':'logistica'}[sectionKey] || sectionKey;
        if (AuthManager.hasModule(module)) return true;
        const children = this.moduleGroups[sectionKey];
        if (children?.some(id => AuthManager.hasModule(id))) return true;

        const entry = this.matrix[sectionKey];
        if (!entry) return true;
        return !!entry[user.role];
    },

    moduleGroups: {
        'academico-dash':['formaciones','plan-estudios','calendario','memorias-asignatura','gestion-tareas','mapa-interactivo','calificaciones','asistencia'],
        'alumnocrm-dash':['alumnado','leads','pre-matricula','entrevistas','email-templates'],
        'rrhh-dash':['profesores','nominas','documentos','justificantes','tutorias-admin','planificacion-mensual'],
        'logistica-dash':['almacen','distribucion']
    },

    // true = el rol actual puede ver el `data-view`/ruta indicada.
    canView(route) {
        const user = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (!user) return false;
        // «Mi nómina»: whoever manages payslips (their «Nóminas» is everybody's) and has a ficha
        // linked to the account; everybody else sees their own under «Nóminas» already.
        if (route === 'mi-nomina') return !!user.professorId && (user.role === 'admin' || user.role === 'rrhh' || AuthManager.hasModule('nominas'));
        // «Planificación mensual»: every contract and class of the staff, so only Administración,
        // RRHH and whoever has its module (or the staff files) — not open by default like other rows.
        if (route === 'planificacion-mensual') return user.role === 'admin' || user.role === 'rrhh' || AuthManager.hasModule('planificacion-mensual') || AuthManager.hasModule('personal-laboral');
        return this.canViewSection(this.keyForRoute(route, user.role));
    }
};

window.RolePermissions = RolePermissions;
